import { request } from '@/api/request';
import type { DatasetResponse, DatasetRequest } from '@/libs/search/types';
import type {
  AssetScanResultWithMetadata,
  Asset,
  AssetStatus,
  AssetScanTypeNote,
  AssetScanNote,
  AssetScanResultFinding,
} from '@/types/models/monitoring';
import type { Severity } from './infractions/types';

export type ScanResultsResponse = DatasetResponse & {
  scanResults: AssetScanResultWithMetadata[];
  totalScanResults: number;
};

export type ScanResultsRequest = DatasetRequest & {
  includeDeletedAssets?: boolean;
  assetStatuses?: AssetStatus[];
};

const getScanResults = (payload: ScanResultsRequest) =>
  request<ScanResultsResponse>('POST', '/external-monitoring/scan-results/list', payload);

const getSupplierScanResults = (supplierID: string, payload: ScanResultsRequest) =>
  request<ScanResultsResponse>(
    'POST',
    `/suppliers/${supplierID}/external-monitoring/scan-results/list`,
    payload,
  );

export type AssetsResponse = {
  assets: Asset[];
  pagination: DatasetResponse;
  total: number;
};
export type AssetsRequest = DatasetRequest & {
  includeDeletedAssets?: boolean;
};

const getAssets = (payload: AssetsRequest) =>
  request<AssetsResponse>('POST', `/external-monitoring/assets/list`, payload);

const getSupplierAssets = (supplierID: string, payload: AssetsRequest) =>
  request<AssetsResponse>(
    'POST',
    `/suppliers/${supplierID}/external-monitoring/assets/list`,
    payload,
  );

export type AddAssetRequest = {
  value: string;
  description?: string;
};
const addAsset = (req: AddAssetRequest) =>
  request<Asset>('POST', '/external-monitoring/assets', req);

export type EditAssetRequest = {
  value: string;
  description: string;
  status: AssetStatus;
};
const editAsset = (assetID: string, req: EditAssetRequest) =>
  request('PUT', `/external-monitoring/assets/${assetID}`, req);

const rescanAsset = (assetID: string, scanTypeId: string) =>
  request('POST', `/external-monitoring/assets/${assetID}/scan-types/${scanTypeId}/scan`);

const deleteAsset = (assetID: string) =>
  request('DELETE', `/external-monitoring/assets/${assetID}`);

export type CreateContextNoteVariables = {
  assetID: string;
  scanTypeID: string;
  note: string;
};
const createContextNote = (vars: CreateContextNoteVariables) =>
  request<AssetScanTypeNote>('POST', '/external-monitoring/scan-type-notes', vars);

const updateContextNote = (noteID: string, note: string) =>
  request<AssetScanTypeNote>('PUT', `/external-monitoring/scan-type-notes/${noteID}`, { note });

const deleteContextNote = (noteID: string) =>
  request('DELETE', `/external-monitoring/scan-type-notes/${noteID}`);

const createClientAssetScanNote = (supplierID: string, assetScanID: string, note: string) =>
  request<AssetScanNote>('POST', `/suppliers/${supplierID}/external-monitoring/asset-scan-notes`, {
    assetScanID,
    note,
  });

const updateClientAssetScanNote = (noteID: string, note: string) =>
  request<AssetScanNote>('PUT', `/external-monitoring/asset-scan-notes/${noteID}`, { note });

const deleteClientAssetScanNote = (noteID: string) =>
  request<AssetScanNote>('DELETE', `/external-monitoring/asset-scan-notes/${noteID}`);

export type Allowance = {
  allowanceType: string;
  allowanceAmount: number;
};

const getAllowances = () => request<Allowance[]>('GET', '/allowances');

export type FindingsResponse = {
  findings: AssetScanResultFinding[];
} & DatasetResponse;

export type FindingsRequest = DatasetRequest;

const getLatestScanResultFindings = (payload: FindingsRequest) =>
  request<FindingsResponse>(
    'POST',
    '/external-monitoring/latest-scan-result-findings/list',
    payload,
  );

const getLatestScanResultFindingsForSupplier = (supplierID: string, payload: FindingsRequest) =>
  request<FindingsResponse>(
    'POST',
    `/suppliers/${supplierID}/external-monitoring/latest-scan-result-findings/list`,
    payload,
  );

export type ScanResultFindingStats = {
  total: number;
  totalFindingsBySeverity: Record<Severity, number>;
};

const getLatestScanResultFindingsStatsForSupplier = (supplierID: string) =>
  request<ScanResultFindingStats>(
    'GET',
    `/suppliers/${supplierID}/external-monitoring/latest-scan-result-findings/stats`,
  );

const getLatestScanResultFindingsStats = () =>
  request<ScanResultFindingStats>('GET', `/external-monitoring/latest-scan-result-findings/stats`);

const verifyAssets = (assetIds: string[]) =>
  request(
    'POST',
    '/external-monitoring/assets/verify',
    assetIds.map((id) => ({ id })),
  );

const rejectAssets = (assetIds: string[]) =>
  request(
    'POST',
    '/external-monitoring/assets/reject',
    assetIds.map((id) => ({ id })),
  );

const api = {
  getScanResults,
  getSupplierScanResults,
  getAssets,
  getSupplierAssets,
  addAsset,
  editAsset,
  rescanAsset,
  deleteAsset,
  createContextNote,
  updateContextNote,
  deleteContextNote,
  createClientAssetScanNote,
  updateClientAssetScanNote,
  deleteClientAssetScanNote,
  getAllowances,
  getLatestScanResultFindings,
  getLatestScanResultFindingsForSupplier,
  getLatestScanResultFindingsStatsForSupplier,
  getLatestScanResultFindingsStats,
  verifyAssets,
  rejectAssets,
};
export default api;

export const replaceAPI = (newApi: Record<string, any>) => {
  Object.keys(newApi).forEach((i) => {
    (api as any)[i] = newApi[i];
  });
};
